<script setup lang="ts">
import { toRaw } from 'vue';

import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';
import { useZindex } from '@/hooks/useZindex';

import AiAdviserButton from '@/domains/student-app/components/adviser/AiAdviserButton.vue';
import Button from '@/components/button/Button.vue';
import FocusTrap from '@/components/focusTrap/FocusTrap.vue';
import IconButton from '@/components/button/IconButton.vue';

import Times from '@/icons/line/times.svg';

export type DialogProps = {
  aiAdviserMessage?: string;
  cancelButtonColor?: ButtonColor | keyof typeof ButtonColor;
  cancelButtonDisabled?: boolean;
  cancelButtonIconLeft?: Component;
  cancelButtonLabel?: string | null;
  cancelButtonLoading?: boolean;
  confirmButtonColor?: ButtonColor | keyof typeof ButtonColor;
  confirmButtonDisabled?: boolean;
  confirmButtonIconLeft?: Component;
  confirmButtonLabel?: string;
  confirmButtonVariant?: ButtonVariant | keyof typeof ButtonVariant;
  contentClass?: string;
  description?: string;
  fit?: 'hug' | 'fill';
  footerHelperText?: string;
  formId?: string;
  headerIcon?: Component;
  hideFooter?: boolean;
  hideOverlay?: boolean;
  icon?: Component;
  isLoading?: boolean;
  isOpen?: boolean;
  isStacked?: boolean;
  message?: string;
  showAiAdviser?: boolean;
  step?: number;
  steps?: number;
  title: string;
  variant?: 'default' | 'danger' | 'warning';
};

const props = withDefaults(defineProps<DialogProps>(), {
  aiAdviserMessage: undefined,
  cancelButtonLabel: 'Cancel',
  cancelButtonColor: ButtonColor.slate,
  cancelButtonIconLeft: undefined,
  confirmButtonLabel: 'Save changes',
  confirmButtonColor: ButtonColor.slate,
  confirmButtonIconLeft: undefined,
  confirmButtonVariant: ButtonVariant.solid,
  fit: 'hug',
  hideFooter: false,
  hideOverlay: false,
  isOpen: false,
  isStacked: false,
  showAiAdviser: false,
  variant: 'default',
  contentClass: 'md:max-w-[475px]'
});

const emit = defineEmits<{
  onCancel: [void];
  onClose: [void];
  onConfirm: [void];
}>();

const rawIcon = toRaw(props.icon);
const rawHeaderIcon = toRaw(props.headerIcon);

const { nextIndex } = useZindex();
const zIndex = nextIndex();

const computedProgressWidth = computed(() => {
  if (!props.step || !props.steps) {
    return '0%';
  }
  return `${(props.step / props.steps) * 100}%`;
});

const computedButtonColor = computed(() => {
  const colorMap: Record<string, 'danger' | 'primary' | 'slate'> = {
    default: 'primary',
    danger: 'danger',
    warning: 'slate'
  };
  return props.confirmButtonColor || colorMap[props.variant] || undefined;
});

function onCancelButtonClick() {
  emit('onCancel');
}

function onOverlayClick() {
  emit('onClose');
}

function onCloseButtonClick() {
  emit('onClose');
}

function onSubmit() {
  emit('onConfirm');
}
</script>

<template>
  <teleport to="body">
    <Transition
      ariaLive="assertive"
      enterActiveClass="transitionSlideUpAndFade duration-200"
      leaveActiveClass="transition-slide-up-and-fade duration-200 absolute inset-x-0 top-0"
      enterFromClass="opacity-50"
      leaveToClass="opacity-0"
      class="fixed"
    >
      <div v-if="isOpen">
        <FocusTrap>
          <form @submit.prevent="onSubmit" class="md:px-4">
            <div
              class="fixed inset-0 data-[state=open]:animate-overlay-show"
              :class="{
                'bg-transparent': hideOverlay,
                'bg-black/70': !hideOverlay
              }"
              @click="onOverlayClick"
              :style="{ zIndex }"
            />

            <div
              class="pointer-events-none fixed bottom-0 left-0 right-0 flex w-full flex-col items-center md:bottom-auto md:left-[50%] md:right-auto md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%] md:overflow-y-auto md:px-4"
              :class="{
                'before:-mb-3 before:h-6 before:w-full before:rounded-t-xl before:bg-zinc-500  md:before:hidden':
                  isStacked,
                'top-6': fit === 'fill',
                'max-h-screen pt-6 md:pt-0': fit === 'hug'
              }"
              :style="{ zIndex }"
            >
              <div
                class="pointer-events-auto flex w-full shrink flex-col rounded-t-xl bg-white shadow-xl focus:outline-none data-[state=open]:animate-content-show md:max-h-[85vh] md:rounded-lg md:border md:border-slate-200"
                :class="[
                  ...[contentClass],
                  {
                    'h-full md:h-auto': fit === 'fill',
                    'max-h-[calc(100vh-24px)]': fit === 'hug'
                  }
                ]"
              >
                <div
                  class="relative flex items-center justify-between gap-x-2 border-b border-slate-200 p-5 pr-2.5 md:pb-4 md:pr-4"
                >
                  <div class="flex grow items-center gap-x-3 md:gap-y-1">
                    <slot name="header">
                      <div
                        v-if="rawHeaderIcon"
                        class="flex h-7 w-7 items-center justify-center rounded border border-slate-300 bg-slate-200"
                      >
                        <component :is="rawHeaderIcon" class="h-4 w-4 text-slate-700" />
                      </div>
                      <div class="flex grow flex-col gap-y-0.5 md:gap-y-1">
                        <div class="text-base font-semibold leading-5 text-slate-900">
                          {{ title }}
                        </div>
                        <div
                          v-if="description"
                          class="text-sm leading-4 text-slate-500 md:leading-5"
                        >
                          {{ description }}
                        </div>
                      </div>
                      <AiAdviserButton
                        v-if="showAiAdviser"
                        :message="aiAdviserMessage"
                        :isStacked="true"
                        class="-my-3 ml-3 md:my-0"
                      />
                    </slot>
                  </div>
                  <IconButton
                    :icon="Times"
                    :size="ButtonSize.md"
                    :variant="ButtonVariant.invisible"
                    ariaLabel="Close dialog"
                    class="hidden md:block"
                    @click="onCloseButtonClick"
                  />
                  <IconButton
                    :icon="Times"
                    :size="ButtonSize.lg"
                    :variant="ButtonVariant.invisible"
                    ariaLabel="Close dialog"
                    class="-my-3 block md:hidden"
                    @click="onCloseButtonClick"
                  />
                  <div
                    v-if="steps"
                    class="absolute inset-x-0 -bottom-px flex h-0.5 w-full items-stretch bg-slate-200"
                  >
                    <div class="bg-primary-700" :style="{ width: computedProgressWidth }" />
                  </div>
                </div>
                <div
                  v-if="$slots.default || $slots.message || $slots.body || message"
                  class="flex grow flex-col gap-y-4 overflow-auto px-5 py-6"
                >
                  <div class="flex items-start gap-2">
                    <div
                      v-if="rawIcon"
                      class="rounded-full p-1"
                      :class="{
                        'bg-slate-100': variant === 'default',
                        'bg-red-100': variant === 'danger',
                        'bg-amber-100': variant === 'warning'
                      }"
                    >
                      <component
                        :is="rawIcon"
                        class="h-4.5 w-4.5"
                        :class="{
                          'text-slate-500': variant === 'default',
                          'text-red-500': variant === 'danger',
                          'text-amber-700': variant === 'warning'
                        }"
                      />
                    </div>
                    <slot>
                      <div class="whitespace-pre-line text-sm leading-5 text-slate-500">
                        <slot name="message">{{ message }}</slot>
                      </div>
                    </slot>
                  </div>
                  <div v-if="$slots.body">
                    <slot name="body" />
                  </div>
                </div>
                <div v-if="$slots.task">
                  <slot name="task" />
                </div>
                <div class="flex grow flex-col overflow-auto" v-if="$slots.chat">
                  <slot name="chat" />
                </div>
                <div class="grow overflow-auto px-5 pb-8 pt-6" v-if="$slots.form">
                  <slot name="form" />
                </div>
                <slot name="footer">
                  <div
                    v-if="!hideFooter"
                    class="flex w-full flex-col justify-stretch gap-x-6 gap-y-4 border-t border-slate-200 px-5 py-2.5 md:flex-row md:flex-wrap md:items-center md:justify-between"
                  >
                    <slot
                      v-if="footerHelperText || $slots.footerHelperText"
                      name="footerHelperText"
                    >
                      <p class="min-w-48 flex-1 text-sm font-semibold text-slate-700">
                        {{ footerHelperText }}
                      </p>
                    </slot>
                    <div
                      class="flex w-full flex-wrap-reverse gap-x-2.5 gap-y-3 md:ml-auto md:w-auto md:justify-end md:gap-x-2 md:gap-y-2"
                    >
                      <slot name="footer">
                        <Button
                          v-if="cancelButtonLabel"
                          class="flex-1 md:hidden md:flex-none"
                          :color="cancelButtonColor"
                          :iconLeft="cancelButtonIconLeft"
                          :isDisabled="cancelButtonDisabled"
                          :isLoading="cancelButtonLoading"
                          :size="ButtonSize.lg"
                          :variant="ButtonVariant.soft"
                          @click="onCancelButtonClick"
                        >
                          {{ cancelButtonLabel }}
                        </Button>
                        <Button
                          v-if="confirmButtonLabel"
                          class="flex-1 md:hidden md:flex-none"
                          :color="computedButtonColor"
                          :iconLeft="confirmButtonIconLeft"
                          :isDisabled="isLoading || confirmButtonDisabled"
                          :isLoading="isLoading"
                          :size="ButtonSize.lg"
                          :variant="confirmButtonVariant"
                          type="submit"
                        >
                          {{ confirmButtonLabel }}
                        </Button>
                        <Button
                          v-if="cancelButtonLabel"
                          class="hidden flex-1 md:inline-flex md:flex-none"
                          :color="cancelButtonColor"
                          :iconLeft="cancelButtonIconLeft"
                          :isDisabled="cancelButtonDisabled"
                          :isLoading="cancelButtonLoading"
                          :size="ButtonSize.sm"
                          :variant="ButtonVariant.soft"
                          @click="onCancelButtonClick"
                        >
                          {{ cancelButtonLabel }}
                        </Button>
                        <Button
                          v-if="confirmButtonLabel"
                          class="hidden flex-1 md:inline-flex md:flex-none"
                          :color="computedButtonColor"
                          :iconLeft="confirmButtonIconLeft"
                          :isDisabled="isLoading || confirmButtonDisabled"
                          :isLoading="isLoading"
                          :size="ButtonSize.sm"
                          :variant="confirmButtonVariant"
                          type="submit"
                        >
                          {{ confirmButtonLabel }}
                        </Button>
                      </slot>
                    </div>
                  </div>
                </slot>
              </div>
            </div>
          </form>
        </FocusTrap>
      </div>
    </Transition>
  </teleport>
</template>

<style>
.test::before {
  @apply -mb-3 h-6 w-full rounded-t-xl bg-slate-200;
  content: '';
}
</style>
